"use client";

import React, { useMemo } from "react";
import { useCompetitionHomeData } from "../../../context/CompetitionHomeDataContext";
import { ArticleBentoGrid } from "../../../partials/articles/ArticleBentoGrid";
import { ArticleGrid } from "../../../partials/articles/ArticleGrid";
import { ArticlePyramid } from "../../../partials/articles/ArticlePyramid";
import { MatchCarousel } from "../../../partials/matches/MatchCarousel";
import { StandingsCompact } from "../../../partials/standings/StandingsCompact";
import { LeadersTables } from "../../../partials/players/LeadersTables";
import { SeasonStatsCards } from "../../../partials/stats/SeasonStatsCards";
import { AdSlot } from "@/features/ads/AdSlot";
import { PromoSlot } from "@/features/promo-slots/PromoSlot";
import { SectionStrip, type BrandedStyle } from "@/components/layout/SectionStrip";
import { HOME_HERO, HOME_MATCHES, HOME_ARTICLES, HOME_LEADERS, HOME_STATS } from "@/features/theme/sectionKeys";
import { FeaturedContentBlock, SectionHeader, CTAButton, type ImageComponent } from "fansunited-sports-ui";
import { FillImage } from "@/components/ui/FillImage";
import { MediaVariantsProvider } from "@/components/ui/MediaVariantsProvider";
import { getPostImageUrl } from "@/components/ui/PostImage";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { HighlightedMatches } from "@/views/home/variants/NewsBlogHomepage/Classic/components/HighlightedMatches";
import { pickHighlightedMatches } from "@/views/home/variants/NewsBlogHomepage/Classic/utils/pickHighlightedMatches";

interface FootballCompetitionHomeNewsPortalLayoutProps {
    videoShortsSlot?: React.ReactNode;
    videoHighlightsSlot?: React.ReactNode;
    sectionStyles?: Record<string, BrandedStyle>;
}

export function FootballCompetitionHomeNewsPortalLayout({
    videoShortsSlot,
    sectionStyles = {},
}: FootballCompetitionHomeNewsPortalLayoutProps) {
    const {
        latestNews,
        featuredPosts,
        leaders,
        promos,
        allStageStandings,
        standings,
        homeSlices,
        competitionStats,
        goalDistribution,
        hasCompetitionStats,
    } = useCompetitionHomeData();
    const homeLabels = useLabels().homePage;
    const nav = useNavigation();

    const topStandings = useMemo(() => standings?.entries ?? [], [standings]);

    // Split articles into non-overlapping ranges — spotlight uses featured if available
    const spotlightSource = featuredPosts?.length ? featuredPosts : latestNews;
    const spotlightPosts = useMemo(() => spotlightSource.slice(0, 6), [spotlightSource]);
    const featuredItems = useMemo(
        () =>
            spotlightPosts.map((post) => ({
                content: {
                    id: String(post.id),
                    type: "article" as const,
                    link: nav.post(post),
                    title: post.title,
                    description: post.summary || undefined,
                    image: {
                        url: getPostImageUrl(post, "medium"),
                        alt: post.title,
                    },
                    gradient: "linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.5) 30%, transparent 50%)",
                },
            })),
        [spotlightPosts, nav]
    );
    // Each section only fills if the previous one is complete
    const sidebarArticles = latestNews.length >= 6 ? latestNews.slice(6, 10) : [];
    const bentoArticles = latestNews.length >= 10 ? latestNews.slice(10, 18) : [];
    const listArticles = latestNews.length >= 18 ? latestNews.slice(18, 22) : [];

    const highlightedMatches = useMemo(
        () => pickHighlightedMatches(homeSlices.nextUpcoming, topStandings, 3),
        [homeSlices.nextUpcoming, topStandings]
    );

    return (
        <div className="min-h-screen bg-background ">
            <main className="max-w-350 mx-auto px-4 mt-6 space-y-10">
                {/* Ad: content-top */}
                <AdSlot id="content-top" />

                {/* ── Section 1: Match Carousel (upcoming only) ── */}
                {homeSlices.nextUpcoming.length > 0 && (
                    <SectionStrip brandedStyle={sectionStyles?.[HOME_MATCHES]}>
                        <MatchCarousel
                            showRecent={false}
                            cardStyles={{
                                shadow: "none",
                                hoverShadow: "sm",
                                borderColor: "border-border",
                            }}
                        />
                    </SectionStrip>
                )}

                {/* ── Section 2: Latest News (featured content block) ── */}
                {featuredItems.length > 0 && (
                    <SectionStrip brandedStyle={sectionStyles?.[HOME_HERO]}>
                        <section>
                            <SectionHeader
                                entity={{
                                    name: homeLabels.latestNews,
                                    styles: {
                                        typography: {
                                            name: "text-lg md:text-2xl font-black uppercase tracking-wide text-foreground mb-4",
                                        },
                                    },
                                }}
                            />
                            <MediaVariantsProvider
                                media={spotlightPosts.map((p) => p.heroImage)}
                                sizes="(max-width: 1024px) 100vw, 66vw"
                            >
                                <FeaturedContentBlock
                                    variant="spotlight"
                                    items={featuredItems}
                                    ImageComponent={FillImage as ImageComponent}
                                    priority
                                    styles={{
                                        gap: "sm",
                                        cardStyles: {
                                            shadow: "none",
                                            typography: {
                                                title: "text-xl font-bold mb-1!",
                                                description: "text-xs md:text-sm text-white/70 line-clamp-2",
                                            },
                                        },
                                    }}
                                />
                            </MediaVariantsProvider>
                        </section>
                    </SectionStrip>
                )}

                {/* Ad: mid-scroll-1 */}
                <AdSlot id="mid-scroll-1" />

                {/* Promo: after featured news */}
                <PromoSlot row={promos[0] ?? null} />

                {/* ── Section 3: Video Section ── */}
                {videoShortsSlot && (
                    <section>
                        <SectionHeader
                            entity={{
                                name: homeLabels.videos ?? "Videos",
                                styles: {
                                    typography: {
                                        name: "text-lg md:text-2xl font-black uppercase tracking-wide text-foreground mb-4",
                                    },
                                },
                            }}
                        />
                        {videoShortsSlot}
                    </section>
                )}

                {/* ── Section 4: More News (pyramid) + Standings & Featured Matches Sidebar ── */}
                {(sidebarArticles.length > 0 || allStageStandings.length > 0) && (
                    <section>
                        <SectionHeader
                            entity={{
                                name: homeLabels.moreNews ?? "More News",
                                styles: {
                                    typography: {
                                        name: "text-lg md:text-2xl font-black uppercase tracking-wide text-foreground mb-4",
                                    },
                                },
                            }}
                        />
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                            {/* Left: pyramid articles */}
                            <div className="lg:col-span-2">
                                {sidebarArticles.length > 0 && (
                                    <ArticlePyramid
                                        articles={sidebarArticles}
                                        heroSizes="(max-width: 1024px) 100vw, 66vw"
                                    />
                                )}
                            </div>

                            {/* Right: standings + featured matches */}
                            <div className="space-y-4">
                                {allStageStandings.length > 0 && (
                                    <StandingsCompact standings={allStageStandings} showHeader={false} maxRows={10} />
                                )}
                                {highlightedMatches.length > 0 && (
                                    <div>
                                        <SectionHeader
                                            entity={{
                                                name: homeLabels.featuredMatches,
                                                styles: {
                                                    typography: {
                                                        name: "text-sm font-semibold text-foreground",
                                                    },
                                                },
                                            }}
                                            primaryCta={
                                                <CTAButton
                                                    text={homeLabels.allFixtures}
                                                    href={nav.matchesList()}
                                                    variant="custom"
                                                    styles={{
                                                        container:
                                                            "text-sm font-medium text-accent hover:text-accent/80 border-none p-0",
                                                    }}
                                                />
                                            }
                                            styles={{ container: "mb-2" }}
                                        />
                                        <HighlightedMatches matches={highlightedMatches} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </section>
                )}

                {/* Ad: mid-scroll-2 */}
                <AdSlot id="mid-scroll-2" />

                {/* ── Section 5: More Articles (bento grid) ── */}
                {bentoArticles.length > 0 && (
                    <SectionStrip brandedStyle={sectionStyles?.[HOME_ARTICLES]}>
                        <SectionHeader
                            entity={{
                                name: homeLabels.moreArticles ?? "More Articles",
                                styles: {
                                    typography: {
                                        name: "text-lg md:text-2xl font-black uppercase tracking-wide text-primary-foreground mb-6",
                                    },
                                },
                            }}
                        />
                        <ArticleBentoGrid articles={bentoArticles} layout="staggered" />
                    </SectionStrip>
                )}

                {/* Ad: mid-scroll-3 */}
                <AdSlot id="mid-scroll-3" />

                {/* ── Section 6: News List (thumbnail + title) ── */}
                {listArticles.length > 0 && (
                    <ArticleGrid
                        articles={listArticles}
                        title={homeLabels.moreNews ?? "More News"}
                        showCta={true}
                        cardVariant="standardHorizontal"
                        columns={3}
                        showDescription={false}
                    />
                )}

                {/* Promo: before leaders */}
                <PromoSlot row={promos[1] ?? null} />

                {/* ── Section 7: Leaders (Top Scorers, Assisters, Contributors) ── */}
                <SectionStrip brandedStyle={sectionStyles?.[HOME_LEADERS]}>
                    <LeadersTables leaders={leaders} />
                </SectionStrip>

                {/* ── Section 8: Season Stats ── */}
                <SectionStrip brandedStyle={sectionStyles?.[HOME_STATS]}>
                    {hasCompetitionStats && (
                        <SeasonStatsCards
                            competitionStats={competitionStats}
                            goalDistribution={goalDistribution}
                            teamWithMostWins={homeSlices.teamWithMostWins}
                        />
                    )}
                </SectionStrip>

                {/* Promo: bottom */}
                <PromoSlot row={promos[2] ?? null} />

                {/* Ad: content-bottom */}
                <AdSlot id="content-bottom" />

                <div className="h-10"></div>
            </main>
        </div>
    );
}
